// What the invoice sheet reads and keeps, shared by both designs' sheets (InvoiceSheet.tsx in Riverstone,
// InvoiceLayer.tsx in Blueprint): the invoice, the refunds and deposits on it, what was just done, and the
// way to read it again after money moves.
import { useCallback, useEffect, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { invoiceDepositNotes, type Deposit } from '../job/deposits-model.ts';
import { followLive, invoiceSheetFollows } from '../shared/sheet-live.ts';
import { invoiceMoved, type InvoiceSheetData } from './invoice-model.ts';

export interface Refunds {
  refundedCents: number;
  refunds: { refundId: string; amountCents: number; method: string; refundedOn: string; reason: string }[];
}

export function useInvoice(invoiceId: string, onChanged?: () => void) {
  const [inv, setInv] = useState<InvoiceSheetData | null>(null);
  const [refunds, setRefunds] = useState<Refunds | null>(null);
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const r = await client.call<InvoiceSheetData>('invoices.get', { id: invoiceId });
      setInv(r);
      // deposits it doesn't show as paid yet: waiting for it, or held for its job's estimates (QA-794)
      const lists = [{ invoiceId }, ...(r.jobId ? [{ jobId: r.jobId }] : [])].map((q) =>
        client
          .call<{ deposits: Deposit[] }>('deposits.list', q)
          .then((x) => x.deposits)
          .catch(() => [] as Deposit[]),
      );
      void Promise.all(lists).then((ds) => setDeposits(ds.flat()));
      if (r.totalCents - r.balanceCents > 0)
        client
          .call<Refunds>('invoices.refunds', { invoiceId })
          .then(setRefunds)
          .catch(() => setRefunds(null));
    } catch (e) {
      setError(errorText(e));
    }
  }, [invoiceId]);
  useEffect(() => {
    void load();
  }, [load]);
  // left open while the customer signs or pays, it follows the live events as the board does (QA-728)
  useEffect(
    () =>
      followLive(
        (c) => invoiceSheetFollows(c, invoiceId),
        () => void load(),
      ),
    [invoiceId, load],
  );
  const moved = async (words?: string) => {
    if (words) setSaid(words);
    await load();
    // the customer's page under the dialog reads again, so it never shows the old balance (QA-805)
    if (inv) invoiceMoved(window, inv.customerId);
    onChanged?.();
  };
  const depositNotes = inv ? invoiceDepositNotes(deposits, inv.id) : [];
  return { inv, refunds, depositNotes, error, said, setSaid, moved };
}
